/**
 * @file Page d'inscription du hub communautaire Ringo.
 * Collecte le prénom, le nom, l'email et le mot de passe pour créer un nouveau compte utilisateur.
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { getErrorMessage } from '../lib/errorMessages';
import { buildAuthLink, getPostAuthRedirect } from '../lib/authRedirect';
import { APP_ROUTES } from '../lib/routes';

/**
 * Composant de la page d'inscription.
 * Présente un formulaire d'inscription et appelle AuthContext.register() lors de la soumission.
 * Redirige vers /dashboard en cas de succès. Affiche les erreurs de validation en ligne.
 * @returns {JSX.Element}
 */
export default function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = getPostAuthRedirect(location);
  const loginLink = buildAuthLink(APP_ROUTES.login, redirectTo === APP_ROUTES.dashboard ? '' : redirectTo);

  /** Met à jour un seul champ du formulaire par sa clé. */
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  /** Soumet le formulaire d'inscription à l'API. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form.firstName, form.lastName, form.email, form.password);
      navigate(redirectTo, { replace: true });
    } catch (e) {
      setError(getErrorMessage(e, t('register.error_fallback')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="register-page">
      <div className="w-full max-w-sm px-6">
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('register.title')}
        </h1>
        <p className="text-sm text-muted-foreground mb-8">{t('register.subtitle')}</p>
        {error && (
          <div className="mb-4 p-3 text-sm text-red-600 border border-red-200 rounded-sm bg-red-50" data-testid="register-error">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fn" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('register.first_name')}</Label>
              <Input id="fn" value={form.firstName} onChange={(e) => update('firstName', e.target.value)}
                className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring"
                required data-testid="register-first-name-input" />
            </div>
            <div>
              <Label htmlFor="ln" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('register.last_name')}</Label>
              <Input id="ln" value={form.lastName} onChange={(e) => update('lastName', e.target.value)}
                className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring"
                data-testid="register-last-name-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('register.email')}</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
              className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring"
              required data-testid="register-email-input" autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('register.password')}</Label>
            <div className="relative">
              <Input id="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => update('password', e.target.value)}
                className="mt-1.5 rounded-sm border-input focus:ring-1 focus:ring-ring focus:border-ring pr-10"
                required minLength={8} data-testid="register-password-input" autoComplete="new-password" />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 mt-[3px] text-muted-foreground hover:text-foreground transition-colors"
                data-testid="register-toggle-password"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading} data-testid="register-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('register.submit')}
          </Button>
        </form>
        <p className="mt-6 text-sm text-muted-foreground text-center">
          {t('register.already_have_account')}{' '}
          <Link to={loginLink} className="text-foreground font-medium hover:underline" data-testid="login-link">{t('register.sign_in')}</Link>
        </p>
      </div>
    </div>
  );
}
